import { ComponentType, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import { formatCurrency } from "../../../libSlice/materialUiFormats";
import { ElectricBoltOutlined, EuroSymbolOutlined } from "@qivia/ui";
import { colors } from "@qivia/ui/src/styles/figmaColors";
import {
  getElectricChargesAsync,
  selectElectricCharges,
  selectElectricChargesStatus,
} from "../electricCharging/electricChargingSlice";
import {
  getLinkyConsentsAsync,
  selectLinkyConsents,
  selectLinkyConsentsStatus,
} from "../electricCharging/linkyConsentsSlice";
import type { PageTasks, PendingTask } from "./tasksSummary";
import "../../../i18n/i18n";

export const useElectricChargingTasks = (): PageTasks => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const electricCharges = useAppSelector(selectElectricCharges);
  const electricChargesStatus = useAppSelector(selectElectricChargesStatus);
  const linkyConsents = useAppSelector(selectLinkyConsents);
  const linkyConsentsStatus = useAppSelector(selectLinkyConsentsStatus);

  useEffect(() => {
    if (electricChargesStatus === "idle") {
      void dispatch(getElectricChargesAsync());
    }
  }, [dispatch, electricChargesStatus]);

  useEffect(() => {
    if (linkyConsentsStatus === "idle") {
      void dispatch(getLinkyConsentsAsync());
    }
  }, [dispatch, linkyConsentsStatus]);

  const pendingLinkyCount = useMemo(
    () =>
      linkyConsents.filter((consent) => consent.consentStatus === "PENDING")
        .length,
    [linkyConsents],
  );

  const chargesToRefund = useMemo(
    () =>
      electricCharges.filter((charge) => charge.refundStatus === "TO_REFUND"),
    [electricCharges],
  );

  const refundTotal = useMemo(
    () =>
      chargesToRefund.reduce(
        (sum, charge) => sum + (charge.refundAmount ?? 0),
        0,
      ),
    [chargesToRefund],
  );

  const tasks: PendingTask[] = useMemo(() => {
    const result: PendingTask[] = [];

    if (pendingLinkyCount > 0) {
      result.push({
        id: "linkyToRelaunch",
        icon: ElectricBoltOutlined as ComponentType,
        color: colors["colors/system/warning/warning_normal"],
        title: t("electric_charging.pendingActions.linkyToRelaunch.title", {
          count: pendingLinkyCount,
        }),
        subtitle: t(
          "electric_charging.pendingActions.linkyToRelaunch.subtitle",
        ),
        actionLabel: t("linky_consents.action.resend"),
        targetPath: "electric_charging/linky_connections?filterStatus=PENDING",
      });
    }

    if (chargesToRefund.length > 0) {
      result.push({
        id: "refundToReimburse",
        icon: EuroSymbolOutlined as ComponentType,
        color: colors["colors/system/error/error_normal"],
        title: t("electric_charging.pendingActions.refundToReimburse.title", {
          count: chargesToRefund.length,
        }),
        subtitle: t(
          "electric_charging.pendingActions.refundToReimburse.subtitle",
          { total: formatCurrency(refundTotal) },
        ),
        actionLabel: t(
          "electric_charging.pendingActions.refundToReimburse.action",
        ),
        targetPath:
          "electric_charging/charging_tracking?filterStatus=TO_REFUND",
      });
    }

    return result;
  }, [pendingLinkyCount, chargesToRefund, refundTotal, t]);

  return { count: tasks.length, tasks };
};
